import React, { useEffect, useRef, useState } from 'react';
import { API_DOMAIN } from '../components/constants';

import dayjs from 'dayjs';
import { Badge, Modal, Progress, Skeleton, Table, Tooltip } from 'antd';
import useSnackbar from "../components/Snackbar/UseSnackbar";
import axios from 'axios';
import letterX from '../assets/images/letter-x.png'
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import confetti from 'canvas-confetti';

require('dayjs/plugin/utc');
dayjs.extend(require('dayjs/plugin/utc'));

const AGENT_COLORS = ['#1d4ed8', '#e85d04', '#6d28d9', '#047857', '#b91c1c', '#0369a1', '#92400e', '#065f46'];

const DonutChart = ({ data, size = 110, thickness = 22 }) => {
    const r = (size - thickness) / 2;
    const cx = size / 2;
    const cy = size / 2;
    const circ = 2 * Math.PI * r;
    const total = data.reduce((s, d) => s + (d.value || 0), 0);
    let cumulative = 0;
    return (
        <svg width={size} height={size} style={{ display: 'block', margin: '0 auto', transform: 'rotate(-90deg)' }}>
            {total === 0
                ? <circle r={r} cx={cx} cy={cy} fill="none" stroke="#e5e7eb" strokeWidth={thickness} />
                : data.map((seg, i) => {
                    const frac = (seg.value || 0) / total;
                    const dash = frac * circ;
                    const offset = circ - cumulative * circ;
                    cumulative += frac;
                    return (
                        <circle key={i} r={r} cx={cx} cy={cy} fill="none"
                            stroke={seg.color} strokeWidth={thickness}
                            strokeDasharray={`${dash} ${circ - dash}`}
                            strokeDashoffset={offset}
                        />
                    );
                })
            }
        </svg>
    );
};

function DashboardV2() {

    const { SuccessSnackbar, ErrorSnackbar, InfoSnackbar, WarningSnackbar, ToastContainer } = useSnackbar();

    const [currentDateTime, setCurrentDateTime] = useState(dayjs());

    const [today_hours, setToday_hours] = useState([]);
    const [sessionProgress, setSessionProgress] = useState([]);
    const [today_hours_loading, setToday_hours_loading] = useState(false);

    useEffect(() => {
        const intervalId = setInterval(() => {
            const now = dayjs();
            setCurrentDateTime(now);

            const progress = today_hours.map((session) =>
                calculateElapsedTimePercentage(session.from, session.to, now)
            );
            setSessionProgress(progress);
        }, 1000);

        return () => clearInterval(intervalId);
    }, [today_hours]);

    const timeToSecondsUtc = (datetime) => {
        const time = dayjs.utc(datetime); // Ensure UTC since wati give already TMZ timestamp
        return (time.hour() * 3600) + (time.minute() * 60) + time.second();
    };

    const timeToSeconds = (datetime) => {
        const time = dayjs(datetime);
        return (time.hour() * 3600) + (time.minute() * 60) + time.second();
    };

    const calculateElapsedTimePercentage = (from, to, nowUtc) => {
        const startSeconds = timeToSecondsUtc(from);
        const endSeconds = timeToSecondsUtc(to);
        const nowSeconds = timeToSeconds(nowUtc);

        // Handle overnight sessions
        const totalDuration = endSeconds >= startSeconds
            ? endSeconds - startSeconds
            : (86400 - startSeconds) + endSeconds; // 86400 = seconds in a day

        let elapsedDuration;
        if (nowSeconds >= startSeconds) {
            elapsedDuration = nowSeconds - startSeconds;
        } else if (endSeconds < startSeconds) {
            // Overnight case: if now is past midnight but before end
            elapsedDuration = (86400 - startSeconds) + nowSeconds;
        } else {
            elapsedDuration = 0;
        }

        if (totalDuration <= 0) return 0; // Invalid session
        if (nowSeconds < startSeconds && endSeconds > startSeconds) return 0; // Before start (normal day)
        if (nowSeconds >= endSeconds && endSeconds > startSeconds) return 100; // After end (normal day)
        if (endSeconds < startSeconds && nowSeconds < endSeconds) return 100; // Overnight, past end

        const elapsedPercentage = (elapsedDuration / totalDuration) * 100;
        return Math.max(0, Math.min(100, elapsedPercentage)).toFixed(2);
    };

    const twoColorsToRed = {
        '0%': '#04e000',
        '100%': '#ff4242'
    };

    const transformWorkingHours = (workingHours) => {
        const result = { workingTimeData: {} };

        workingHours.workingTimeData.forEach(({ weekDays, from, to }) => {
            // Keep the full timestamps as-is
            const fullFrom = from;
            const fullTo = to;

            // Add the time slot to each day
            weekDays.forEach((day) => {
                const dayNormalized = day.charAt(0).toUpperCase() + day.slice(1).toLowerCase();
                if (!result.workingTimeData[dayNormalized]) {
                    result.workingTimeData[dayNormalized] = [];
                }
                result.workingTimeData[dayNormalized].push({ from: fullFrom, to: fullTo });
            });
        });
        return result?.workingTimeData;
    };

    const load_working_hours = async () => {
        try {
            setToday_hours_loading(true);
            var response = await axios.get(API_DOMAIN + "/whatsapp/get_wati_working_hours");
            if (response?.data?.success) {
                if (response?.data?.data?.holidayMode) {
                    InfoSnackbar(`It's a holiday!`);
                }
                else {
                    const formatted_hours_data = transformWorkingHours(response?.data?.data);
                    const today_hours = formatted_hours_data?.[dayjs().format("dddd")];
                    if (today_hours?.length) {
                        // InfoSnackbar(`There are ${today_hours?.length} Business sessions today`);
                        setToday_hours(today_hours);
                        setSessionProgress(today_hours.map(() => 0));
                    } else {
                        InfoSnackbar(`It's a holiday!`);
                    }
                }
            }
        } catch (error) {
            ErrorSnackbar(error.message);
        }
        finally {
            setToday_hours_loading(false)
        }
    };

    useEffect(() => {
        load_working_hours();
    }, []);

    const extractTime = (datetime) => {
        return datetime && dayjs.utc(datetime).isValid() ? dayjs.utc(datetime).format('hh:mmA') : 'Unknown';
    };

    const [maxDayCurrentMonth, setMaxDayCurrentMonth] = useState([]);
    const [todayDayDate, setTodayDayDate] = useState(null);

    useEffect(() => {
        const currentDate = dayjs();
        const daysInMonth = currentDate.daysInMonth();
        const daysArray = Array.from({ length: daysInMonth }, (_, i) => i + 1);
        setMaxDayCurrentMonth(daysArray);
        setTodayDayDate(currentDate.date())
    }, []);

    const dashboard_reload_interval = 300000; // 5 minutes
    const RELOAD_SECONDS = dashboard_reload_interval / 1000;

    const [refreshTimeBar, setRefreshTimeBar] = useState(0);
    const [isFetching, setIsFetching] = useState(false); // optional for UI


    const [dashboardDataSet, setDashboardDataSet] = useState([]);
    const [operatorPerformanceSet, setoperatorPerformanceSet] = useState([]);
    const [dashboardDataSetLoading, setDashboardDataSetLoading] = useState(false);

    // Sales dashboard — demo data (replace with real API later)
    const DEMO_SALES_DATA = {
        summary: {
            daily_sales:    18450,
            daily_target:   25000,
            monthly_sales:  142800,
            monthly_target: 250000,
        },
        agents: [
            {
                id: 1, name: "Abhilash Kumar", active_chats: 7,
                daily_sales: 6200, daily_target: 8000, monthly_sales: 52000, monthly_target: 80000,
                statuses: { getting_info: 4, getting_price: 2, waiting_confirm: 3, confirmed: 2, out_for_delivery: 1, delivered: 5, inside_warranty: 3 },
            },
            {
                id: 2, name: "Faisal Al Rashidi", active_chats: 5,
                daily_sales: 4750, daily_target: 6000, monthly_sales: 38000, monthly_target: 60000,
                statuses: { getting_info: 2, getting_price: 3, waiting_confirm: 1, confirmed: 3, out_for_delivery: 2, delivered: 3, inside_warranty: 1 },
            },
            {
                id: 3, name: "Mohammed Zain", active_chats: 9,
                daily_sales: 3900, daily_target: 8000, monthly_sales: 29500, monthly_target: 80000,
                statuses: { getting_info: 6, getting_price: 4, waiting_confirm: 2, confirmed: 1, out_for_delivery: 0, delivered: 2, inside_warranty: 0 },
            },
            {
                id: 4, name: "Priya Nair", active_chats: 3,
                daily_sales: 3600, daily_target: 3000, monthly_sales: 23300, monthly_target: 30000,
                statuses: { getting_info: 1, getting_price: 1, waiting_confirm: 2, confirmed: 4, out_for_delivery: 3, delivered: 7, inside_warranty: 5 },
            },
        ],
    };

    const [salesDashboard] = useState(DEMO_SALES_DATA);
    const salesDashboardLoading = false;

    // ── Confetti ──
    const confettiTimerRef = useRef(null);
    const confettiIntervalRef = useRef(null);
    const [confettiMessage, setConfettiMessage] = useState(null);

    const triggerConfetti = (message) => {
        // Clear any running confetti
        if (confettiTimerRef.current) clearTimeout(confettiTimerRef.current);
        if (confettiIntervalRef.current) clearInterval(confettiIntervalRef.current);

        setConfettiMessage(message);

        const fire = () => {
            confetti({ particleCount: 80, spread: 120, origin: { x: Math.random(), y: 0.3 }, colors: ['#1d4ed8', '#16a34a', '#f59e0b', '#ef4444', '#8b5cf6'] });
            confetti({ particleCount: 60, spread: 80,  origin: { x: Math.random(), y: 0.5 } });
        };
        fire();
        confettiIntervalRef.current = setInterval(fire, 1800);

        confettiTimerRef.current = setTimeout(() => {
            clearInterval(confettiIntervalRef.current);
            confettiIntervalRef.current = null;
            setConfettiMessage(null);
            confetti.reset();
        }, 10000);
    };

    // ── Drilldown modal ──
    const [drilldown, setDrilldown] = useState({ open: false, title: '', rows: [] });
    const refreshIntervalRef = useRef(null); // setInterval for the progress bar
    const isFetchingRef = useRef(false);
    const startCountdown = () => {
        // clear any previous interval
        if (refreshIntervalRef.current) {
            clearInterval(refreshIntervalRef.current);
            refreshIntervalRef.current = null;
        }

        let secondsRemaining = RELOAD_SECONDS;
        setRefreshTimeBar(100);

        refreshIntervalRef.current = setInterval(() => {
            // if we’re currently fetching, keep the bar at 0 and do nothing
            if (isFetchingRef.current) return;

            secondsRemaining -= 1;

            const percent = Math.max(
                0,
                Math.min(100, Math.round((secondsRemaining / RELOAD_SECONDS) * 100))
            );
            setRefreshTimeBar(percent);

            if (secondsRemaining <= 0) {
                clearInterval(refreshIntervalRef.current);
                refreshIntervalRef.current = null;

                // trigger fetch ONCE and keep bar at 0 until it resolves
                load_dashboard_data();
            }
        }, 1000);
    };

    const stopCountdown = () => {
        if (refreshIntervalRef.current) {
            clearInterval(refreshIntervalRef.current);
            refreshIntervalRef.current = null;
        }
    };

    // NOTE: no timeout scheduling anymore — countdown controls fetch cadence
    const load_dashboard_data = async () => {
        if (isFetchingRef.current) return; // guard
        isFetchingRef.current = true;
        setIsFetching(true);
        setRefreshTimeBar(0); // show 0% while loading (optional)

        setDashboardDataSetLoading(true);
        try {
            const today = new Date().toISOString().substring(0, 10);
            const res = await axios.post(API_DOMAIN + "/reports/get_analytics", {
                from: `${today}T00:00:00.000Z`,
                to: `${today}T23:59:59.000Z`
            });

            if (res.data.success) {
                setoperatorPerformanceSet(res?.data?.data?.operator_performance?.items);
                setDashboardDataSet(res?.data?.data?.dashboard_status?.ticketTotalData);
            } else {
                ErrorSnackbar("Something went wrong while fetching users list");
            }
        } catch (error) {
            ErrorSnackbar("Something went wrong while fetching users list - " + error.message);
        } finally {
            setDashboardDataSetLoading(false);
            isFetchingRef.current = false;
            setIsFetching(false);

            // restart a fresh countdown only AFTER fetch resolves
            startCountdown();
        }
    };

    useEffect(() => {
        // initial prime: load once, then the countdown will schedule future calls
        load_dashboard_data();
        return () => {
            stopCountdown();
        };
    }, []);



    function formatSecondsToTimeString(seconds) {
        if (!(typeof seconds !== "number" || isNaN(seconds))) {
            const totalSeconds = Math.floor(seconds); // ignore milliseconds
            const days = Math.floor(totalSeconds / (3600 * 24));
            const hours = Math.floor((totalSeconds % (3600 * 24)) / 3600);
            const minutes = Math.floor((totalSeconds % 3600) / 60);
            const secs = totalSeconds % 60;

            return `${days}d ${hours}h ${minutes}m ${secs}s`;
        }

    }

    const [elapsedDayPercentage, setElapsedDayPercentage] = useState(0);

    useEffect(() => {
        const updateTime = () => {
            const now = dayjs(); // Current UTC time
            setCurrentDateTime(now);

            // Start of the day (midnight UTC)
            const startOfDay = now.startOf('day');
            // Total seconds in a day
            const totalSecondsInDay = 24 * 60 * 60; // 86,400 seconds
            // Elapsed seconds from midnight to now
            const elapsedSeconds = now.diff(startOfDay, 'second');
            // Percentage of day elapsed
            const percentage = (elapsedSeconds / totalSecondsInDay) * 100;
            setElapsedDayPercentage(Math.min(100, Math.max(0, percentage)).toFixed(2)); // Cap at 100%
        };

        updateTime(); // Set initial value
        const intervalId = setInterval(updateTime, 10000); // Update every minute

        return () => clearInterval(intervalId); // Cleanup on unmount
    }, []);

    const columns = [
        {
            key: "fullName",
            title: "Agent Name",
            dataIndex: "fullName",
            sorter: (a, b) => a?.fullName?.localeCompare(b?.fullName),
            sortDirections: ["descend", "ascend"],
        },
        {
            key: "openChatsCount",
            title: "Open Tickets",
            dataIndex: "openChatsCount",
            sorter: (a, b) => a?.openChatsCount - b?.openChatsCount,
            sortDirections: ["descend", "ascend"],
        },
        {
            key: "assignedChatsCount",
            title: "Assigned Tickets",
            render: (record) => (
                <div>
                    {record?.assignedChatsCount}
                </div >
            ),
            sorter: (a, b) => a?.assignedChatsCount - b?.assignedChatsCount,
            sortDirections: ["descend", "ascend"],
        },
        {
            key: "pendingChatsCount",
            title: "Pending Tickets",
            render: (record) => (
                <div>
                    {record?.pendingChatsCount}
                </div >
            ),
            sorter: (a, b) => a?.pendingChatsCount - b?.pendingChatsCount,
            sortDirections: ["descend", "ascend"],
        },
        {
            key: "resolvedChatsCount",
            title: "Resolved Tickets",
            render: (record) => (
                <div>
                    {record?.resolvedChatsCount}
                </div >
            ),
            sorter: (a, b) => a?.resolvedChatsCount - b?.resolvedChatsCount,
            sortDirections: ["descend", "ascend"],
        },
        {
            key: "expiredChatsCount",
            title: "Expired Tickets",
            render: (record) => (
                <div>
                    {record?.expiredChatsCount}
                </div >
            ),
            sorter: (a, b) => a?.expiredChatsCount - b?.expiredChatsCount,
            sortDirections: ["descend", "ascend"],
        },
        {
            key: "avgFirstResponseSeconds",
            title: "Avg First Resp Time",
            render: (record) => (
                <div>
                    {formatSecondsToTimeString(record?.avgFirstResponseSeconds)}
                </div >
            ),
            sorter: (a, b) => a?.avgFirstResponseSeconds - b?.avgFirstResponseSeconds,
            sortDirections: ["descend", "ascend"],
        },
        {
            key: "avgResolutionSeconds",
            title: "Avg Resoution Time",
            render: (record) => (
                <div>
                    {formatSecondsToTimeString(record?.avgResolutionSeconds)}
                </div >
            ),
            sorter: (a, b) => a?.avgResolutionSeconds - b?.avgResolutionSeconds,
            sortDirections: ["descend", "ascend"],
        },
        {
            key: "agentAvailabilitySeconds",
            title: "Agent Availability Time",
            render: (record) => (
                <div>
                    {formatSecondsToTimeString(record?.agentAvailabilitySeconds)}
                </div >
            ),
            sorter: (a, b) => a?.agentAvailabilitySeconds - b?.agentAvailabilitySeconds,
            sortDirections: ["descend", "ascend"],
        },
    ];

    return (
        <div>
            <div>
                {/* Clock */}
                <div className='digital7-font mb-1 pl-2 pr-2 rounded flex items-center' style={{ marginTop: "-35px", backgroundColor: "#333333" }}>


                    <div className='justify-between flex w-full'>


                        {/* dates of the month progress */}
                        <div style={{ width: "100%" }} className='w-full pb-2'>
                            {maxDayCurrentMonth.length > 0 ? (
                                <div>
                                    <div className='flex gap-3'>
                                        {/* Clock Widget */}
                                        <div>
                                            <div className='flex justify-center'>
                                                {/* HH */}
                                                <div style={{ fontSize: '4em', width: "53px" }} className='flex relative'>
                                                    <div style={{ color: "#b3b3b3" }} className="absolute top-0 left-0 flex opacity-30 pointer-events-none select-none">
                                                        <div style={{ width: "26.5px" }} className='flex justify-end'>8</div>
                                                        <div style={{ width: "26.5px" }} className='flex justify-end'>8</div>
                                                    </div>
                                                    <div style={{ width: "26.5px" }} className='neon-glow flex justify-end'>
                                                        {currentDateTime.format('hh:mm:ssA DD-MMMM-YYYY').substring(0, 1)}
                                                    </div>
                                                    <div style={{ width: "26.5px" }} className='neon-glow flex justify-end'>
                                                        {currentDateTime.format('hh:mm:ssA DD-MMMM-YYYY').substring(1, 2)}
                                                    </div>
                                                </div>

                                                {/* : */}
                                                <div style={{ fontSize: '4em' }} className='relative'>
                                                    <div style={{ color: "#b3b3b3" }} className="absolute top-0 left-0 opacity-30">{currentDateTime.format('hh:mm:ssA DD-MMMM-YYYY').substring(2, 3)}
                                                    </div>
                                                    <div className="relative z-10 neon-glow flash">
                                                        {currentDateTime.format('hh:mm:ssA DD-MMMM-YYYY').substring(2, 3)}
                                                    </div>
                                                </div>

                                                {/* MM */}
                                                <div style={{ fontSize: '4em', width: "53px" }} className='flex relative'>
                                                    <div style={{ color: "#b3b3b3" }} className="absolute top-0 left-0 flex opacity-30 pointer-events-none select-none">
                                                        <div style={{ width: "26.5px" }} className='flex justify-end'>8</div>
                                                        <div style={{ width: "26.5px" }} className='flex justify-end'>8</div>
                                                    </div>
                                                    <div style={{ width: "26.5px" }} className='neon-glow flex justify-end'>
                                                        {currentDateTime.format('hh:mm:ssA DD-MMMM-YYYY').substring(3, 4)}
                                                    </div>
                                                    <div style={{ width: "26.5px" }} className='neon-glow flex justify-end'>
                                                        {currentDateTime.format('hh:mm:ssA DD-MMMM-YYYY').substring(4, 5)}
                                                    </div>
                                                </div>

                                                {/* : */}
                                                <div style={{ fontSize: '4em' }} className='relative'>
                                                    <div style={{ color: "#b3b3b3" }} className="absolute top-0 left-0 opacity-30">{currentDateTime.format('hh:mm:ssA DD-MMMM-YYYY').substring(2, 3)}
                                                    </div>
                                                    <div className="relative z-10 neon-glow flash">
                                                        {currentDateTime.format('hh:mm:ssA DD-MMMM-YYYY').substring(2, 3)}
                                                    </div>
                                                </div>

                                                {/* SS */}
                                                <div style={{ fontSize: '4em', width: "53px" }} className='flex relative'>
                                                    <div style={{ color: "#b3b3b3" }} className="absolute top-0 left-0 flex opacity-30 pointer-events-none select-none">
                                                        <div style={{ width: "26.5px" }} className='flex justify-end'>8</div>
                                                        <div style={{ width: "26.5px" }} className='flex justify-end'>8</div>
                                                    </div>
                                                    <div style={{ width: "26.5px" }} className='neon-glow flex justify-end'>
                                                        {currentDateTime.format('hh:mm:ssA DD-MMMM-YYYY').substring(6, 7)}
                                                    </div>
                                                    <div style={{ width: "26.5px" }} className='neon-glow flex justify-end'>
                                                        {currentDateTime.format('hh:mm:ssA DD-MMMM-YYYY').substring(7, 8)}
                                                    </div>
                                                </div>

                                                {/* AM/PM */}
                                                <div style={{ fontSize: "2em" }} className='flex items-center mt-3 relative'>
                                                    <div style={{ color: "#b3b3b3" }} className="absolute top-3.4 left-0 opacity-30 select-none pointer-events-none">
                                                        88
                                                    </div>
                                                    <div className="relative z-10 neon-glow">
                                                        {currentDateTime.format('hh:mm:ssA DD-MMMM-YYYY').substring(8, 10)}
                                                    </div>
                                                </div>
                                            </div>

                                        </div>
                                        {/* DATE */}
                                        <div style={{ fontSize: "4em" }} className="flex justify-center relative">
                                            <div style={{ width: "53px" }} className='flex relative'>
                                                <div style={{ color: "#b3b3b3" }} className="absolute top-0 left-0 flex opacity-30 pointer-events-none select-none">
                                                    <div style={{ width: "26.5px" }} className='flex justify-end'>8</div>
                                                    <div style={{ width: "26.5px" }} className='flex justify-end'>8</div>
                                                </div>
                                                <div style={{ width: "26.5px" }} className='neon-glow flex justify-end'>
                                                    {currentDateTime.format('hh:mm:ssA DD-MMMM-YYYY').substring(11, 12)}
                                                </div>
                                                <div style={{ width: "26.5px" }} className='neon-glow flex justify-end'>
                                                    {currentDateTime.format('hh:mm:ssA DD-MMMM-YYYY').substring(12, 13)}
                                                </div>
                                            </div>


                                            {/* - */}
                                            <div className='flex'>
                                                <div style={{ color: "#b3b3b3" }} className="absolute top-0 left-200 opacity-30 select-none pointer-events-none" >
                                                    -
                                                </div>
                                                <div className="relative z-10 neon-glow">
                                                    {currentDateTime.format('hh:mm:ssA DD-MMMM-YYYY').substring(13, 14)}
                                                </div>
                                            </div>

                                            {/* name of the month */}
                                            <div className='flex'>
                                                {
                                                    (currentDateTime.format('hh:mm:ssA DD-MMMM-YYYY').substring(currentDateTime.format('hh:mm:ssA DD-MMMM-YYYY').indexOf("-") + 1, currentDateTime.format('hh:mm:ssA DD-MMMM-YYYY').lastIndexOf("-"))).split("").map((e) => {
                                                        return (
                                                            <div style={{ width: "26.5px" }} className='flex relative'>
                                                                <div style={{ color: "#b3b3b3" }} className="absolute top-0 left-0 flex opacity-30 pointer-events-none select-none">
                                                                    <div style={{ width: "26.5px" }} className='flex justify-end'>8</div>
                                                                </div>
                                                                <div style={{ width: "26.5px" }} className='neon-glow flex justify-end'>
                                                                    {e}
                                                                </div>
                                                            </div>
                                                        )
                                                    })
                                                }
                                            </div>

                                            {/* - */}
                                            <div className='flex'>
                                                <div style={{ color: "#b3b3b3" }} className="absolute top-0 left-200 opacity-30 select-none pointer-events-none" >
                                                    -
                                                </div>
                                                <div className="relative z-10 neon-glow">
                                                    {currentDateTime.format('hh:mm:ssA DD-MMMM-YYYY').substring(13, 14)}
                                                </div>
                                            </div>

                                            {/* year */}
                                            <div className='flex'>
                                                <div style={{ color: "#b3b3b3" }} className="absolute top-0 left-200 opacity-30 select-none pointer-events-none" >
                                                    8888
                                                </div>
                                                <div className="relative z-10 neon-glow">
                                                    {currentDateTime.format('hh:mm:ssA DD-MMMM-YYYY').substring((currentDateTime.format('hh:mm:ssA DD-MMMM-YYYY').length - 4), currentDateTime.format('hh:mm:ssA DD-MMMM-YYYY').length)}
                                                </div>
                                            </div>

                                        </div>

                                        {/* shift progressbar */}
                                        <div className='flex gap-3 ml-2 mt-2 mb-2 p-1 border rounded w-full drop-shadow' style={{ backgroundColor: "#ebebeb" }}>
                                            {today_hours.length > 0 ? today_hours.map((item, index) => (
                                                <div key={index} style={{ width: "100%" }}>
                                                    <div className='flex justify-center'>SHIFT {index + 1}</div>
                                                    <Progress percent={sessionProgress[index] || 0} strokeColor={twoColorsToRed} />
                                                    <div className='flex justify-between pr-10 pl-1'>
                                                        <div>{extractTime(item?.from)}</div>
                                                        <div className={sessionProgress[index] > 0 && sessionProgress[index] <= 100 && sessionProgress[index] != 100 ? 'animate-arrows' : ""}>{`>>>>`}</div>
                                                        <div>{extractTime(item?.to)}</div>
                                                    </div>
                                                </div>
                                            )) : today_hours_loading ? (<div style={{ width: "100%" }} className='flex justify-center loading-text'>Loading<span className="dots"></span></div>) : (<div style={{ width: "100%" }} className='flex justify-center'>IT'S A HOLIDAY!</div>)}
                                        </div>
                                    </div>

                                    {/* month calendar */}
                                    <div className='border flex rounded p-1 flex-wrap justify-center drop-shadow' style={{ backgroundColor: "#ebebeb" }}>

                                        {maxDayCurrentMonth.map((day) => (
                                            <div key={day} style={{ position: 'relative', display: 'inline-block' }}>
                                                <div
                                                    className="border rounded p-1 flex justify-center"
                                                    style={{
                                                        margin: '0 5px',
                                                        backgroundColor: 'white',
                                                        width: '25px',
                                                        height: '25px',
                                                        position: 'relative',
                                                    }}
                                                >
                                                    {day < todayDayDate ? (
                                                        <img
                                                            src={letterX}
                                                            alt="Past day indicator"
                                                            style={{
                                                                position: 'absolute',
                                                                top: '50%',
                                                                left: '50%',
                                                                transform: 'translate(-50%, -50%)',
                                                                width: '20px',
                                                                height: '20px',
                                                                opacity: 0.8,
                                                                pointerEvents: 'none',
                                                                zIndex: 1,
                                                            }}
                                                        />
                                                    ) : day == todayDayDate ? (
                                                        <Tooltip title={`${elapsedDayPercentage}% of the day elapsed`}>
                                                            <div style={{
                                                                width: "20px", height: "20px", position: 'absolute',
                                                                top: '36%',
                                                                left: '50%',
                                                                transform: 'translate(-50%, -50%)',
                                                            }}>
                                                                <Progress
                                                                    type="circle"
                                                                    percent={elapsedDayPercentage}
                                                                    width={20}
                                                                    strokeWidth={10}
                                                                    showInfo={false}
                                                                />
                                                            </div>
                                                        </Tooltip>
                                                    ) : null}
                                                    <span
                                                        style={{
                                                            position: 'relative',
                                                            zIndex: 0, // Number in the back
                                                            lineHeight: '25px',
                                                            width: '100%',
                                                            textAlign: 'center',
                                                            display: 'flex',
                                                            justifyContent: 'center',
                                                            alignItems: 'center',
                                                            fontWeight: "550"
                                                        }}
                                                    >
                                                        {day}
                                                    </span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    </div>

                </div>


                {/* Top cards */}
                {/* <div className="flex mt-2 gap-3 flex-wrap justify-center">
                    <div className='border rounded ' style={{ backgroundColor: "#fcfcfc" }}>
                        <div className="rounded p-3 m-2 flex justify-between items-center" style={{ backgroundColor: "#e3e3e3", minWidth: "217.5px" }}>
                            <div>
                                <div style={{ color: "#8c8c8c" }}>Today's Sales</div>
                                <div className='flex'>
                                    <div style={{ fontSize: "19px", fontWeight: "600", color: "#706f6f" }}>AED&nbsp;</div>
                                    <div style={{ fontSize: "19px", fontWeight: "600" }}> 41250</div>
                                </div>
                            </div>
                            <div className="flex items-center justify-center">
                                <div className="rounded p-1 flex justify-center" style={{ color: "white", backgroundColor: "#22a10b", fontSize: "16px", minWidth: "40px", minHeight: "40px" }}><LineChartOutlined /></div>
                            </div>
                        </div>
                    </div>

                    <div className='border rounded flex flex-wrap ' style={{ backgroundColor: "#fcfcfc", minWidth: "auto" }}>
                        <div>
                            <div className="rounded p-3 m-2 flex justify-between items-center" style={{ backgroundColor: "#e3e3e3", minWidth: "217.5px" }}>
                                <div>
                                    <div style={{ color: "#8c8c8c" }}>Today's Total Tickets</div>
                                    <div style={{ fontSize: "19px", fontWeight: "600" }}>55</div>
                                </div>
                                <div className="flex items-center">
                                    <div className="rounded p-1 flex justify-center " style={{ color: "white", backgroundColor: "#1890ff", fontSize: "16px", minWidth: "40px", minHeight: "40px" }}><FileTextOutlined /></div>
                                </div>
                            </div>
                        </div>
                        <div>
                            <div className="rounded p-3 m-2 flex justify-between items-center" style={{ backgroundColor: "#e3e3e3", minWidth: "217.5px" }}>
                                <div>
                                    <div style={{ color: "#8c8c8c" }}>Active Tickets</div>
                                    <div style={{ fontSize: "19px", fontWeight: "600" }}>35</div>
                                </div>
                                <div className="flex items-center">
                                    <div className="rounded p-1 flex justify-center " style={{ color: "white", backgroundColor: "#23eb00", fontSize: "16px", minWidth: "40px", minHeight: "40px" }}><FileSearchOutlined /></div>
                                </div>
                            </div>
                        </div>
                        <div>
                            <div className="rounded p-3 m-2 flex justify-between items-center" style={{ backgroundColor: "#e3e3e3", minWidth: "217.5px" }}>
                                <div>
                                    <div style={{ color: "#8c8c8c" }}>Closed Tickets</div>
                                    <div style={{ fontSize: "19px", fontWeight: "600" }}>25</div>
                                </div>
                                <div className="flex items-center">
                                    <div className="rounded p-1 flex justify-center " style={{ color: "white", backgroundColor: "#eba926", fontSize: "16px", minWidth: "40px", minHeight: "40px" }}><FileProtectOutlined /></div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className='border rounded ' style={{ backgroundColor: "#fcfcfc" }}>
                        <div className="rounded p-3 m-2 flex justify-between items-center" style={{ backgroundColor: "#e3e3e3", minWidth: "217.5px" }}>
                            <div>
                                <div style={{ color: "#8c8c8c" }}>Agents Online</div>
                                <div style={{ fontSize: "19px", fontWeight: "600" }}>2</div>
                            </div>
                            <div className="flex items-center justify-center">
                                <div className="rounded p-1 flex justify-center" style={{ color: "white", backgroundColor: "#1890ff", fontSize: "16px", minWidth: "40px", minHeight: "40px" }}><TeamOutlined /></div>
                            </div>
                        </div>
                    </div>
                </div> */}

                {/* <div className='border shadow-3 rounded mt-2 pt-1 pb-1 pr-5 pl-5 flex justify-between'>

                    <div className="flex items-center gap-1 border drop-shadow-1 rounded pr-2 pl-2 pt-0.5 pb-0.5" style={{ color: "black", backgroundColor: "white" }}>
                        <Badge
                            overflowCount={9999999999}
                            count={dashboardDataSet?.newTicket || 0}
                            showZero
                            color="green"
                        />
                        New Tickets
                    </div>

                    <div className="flex items-center gap-1 border drop-shadow-1 rounded pr-2 pl-2 pt-0.5 pb-0.5" style={{ color: "black", backgroundColor: "white" }}>
                        <Badge
                            overflowCount={9999999999}
                            count={dashboardDataSet?.openTicket || 0}
                            showZero
                            color="green"
                        />
                        Open Tickets
                    </div>

                    <div className="flex items-center gap-1 border drop-shadow-1 rounded pr-2 pl-2 pt-0.5 pb-0.5" style={{ color: "black", backgroundColor: "white" }}>
                        <Badge
                            overflowCount={9999999999}
                            count={dashboardDataSet?.pending || 0}
                            showZero
                            color="green"
                        />
                        Pending Tickets
                    </div>

                    <div className="flex items-center gap-1 border drop-shadow-1 rounded pr-2 pl-2 pt-0.5 pb-0.5" style={{ color: "black", backgroundColor: "white" }}>
                        <Badge
                            overflowCount={9999999999}
                            count={dashboardDataSet?.solved || 0}
                            showZero
                            color="green"
                        />
                        Solved Tickets
                    </div>

                    <div className="flex items-center gap-1 border drop-shadow-1 rounded pr-2 pl-2 pt-0.5 pb-0.5" style={{ color: "black", backgroundColor: "white" }}>
                        <Badge
                            overflowCount={9999999999}
                            count={dashboardDataSet?.solvedByBot || 0}
                            showZero
                            color="green"
                        />
                        Solved By Bot
                    </div>

                </div>
                <div style={{ translate: "0px 19px", position: "relative", zIndex: "10000", marginTop: "-22px" }} className='flex justify-center'>
                    <Tooltip title="The table will be refreshed everytime the bar goes empty.">
                        <Progress percent={refreshTimeBar} showInfo={false} size={"small"} style={{ width: "99.8%" }} status="active" />
                    </Tooltip>
                </div>
                <Table
                    dataSource={operatorPerformanceSet}
                    columns={columns}
                    loading={dashboardDataSetLoading}
                    bordered={true}
                    pagination={false}
                    className='mt-2 shadow-3 rounded border'
                /> */}

                {/* ── Confetti overlay ── */}
                {confettiMessage && (
                    <div style={{
                        position: 'fixed', inset: 0, zIndex: 99999,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        pointerEvents: 'none',
                    }}>
                        <div style={{
                            background: 'rgba(0,0,0,0.55)', borderRadius: 20,
                            padding: '28px 52px', textAlign: 'center',
                            boxShadow: '0 8px 40px rgba(0,0,0,0.4)',
                            animation: 'srm-banner-slidein 0.4s cubic-bezier(0.22,1,0.36,1) both',
                        }}>
                            <div style={{ fontSize: 52, marginBottom: 8 }}>🎉</div>
                            <div style={{ color: 'white', fontSize: 26, fontWeight: 800, letterSpacing: 1 }}>
                                {confettiMessage}
                            </div>
                        </div>
                    </div>
                )}

                {/* ── Drilldown modal ── */}
                <Modal
                    open={drilldown.open}
                    title={drilldown.title}
                    onCancel={() => setDrilldown(d => ({ ...d, open: false }))}
                    footer={null}
                    width={520}
                >
                    {drilldown.rows.length === 0
                        ? <div style={{ textAlign: 'center', color: '#aaa', padding: '24px 0' }}>No entries to show.</div>
                        : <Table
                            size="small"
                            pagination={false}
                            dataSource={drilldown.rows.map((r, i) => ({ ...r, key: i }))}
                            columns={drilldown.columns || [
                                { title: 'Agent', dataIndex: 'name', key: 'name' },
                                { title: 'Count', dataIndex: 'count', key: 'count', align: 'right' },
                            ]}
                        />
                    }
                </Modal>

                {/* ── Sales Dashboard ── */}
                <div className="mt-2">

                    {/* ── Top summary strip: total daily + monthly ── */}
                    {(() => {
                        const s = salesDashboard?.summary ?? {};
                        const dailyPct  = s.daily_target   ? Math.min(100, +((s.daily_sales   / s.daily_target)   * 100).toFixed(1)) : 0;
                        const monthlyPct = s.monthly_target ? Math.min(100, +((s.monthly_sales  / s.monthly_target) * 100).toFixed(1)) : 0;

                        const openSummaryModal = (key) => {
                            const colMap = {
                                daily_sales:    { title: "Today's Sales — by Agent",    col: 'daily_sales' },
                                daily_target:   { title: "Daily Target — by Agent",     col: 'daily_target' },
                                monthly_sales:  { title: "Month Sales — by Agent",      col: 'monthly_sales' },
                                monthly_target: { title: "Monthly Target — by Agent",   col: 'monthly_target' },
                            };
                            const meta = colMap[key];
                            setDrilldown({
                                open: true,
                                title: meta.title,
                                columns: [
                                    { title: 'Agent', dataIndex: 'name', key: 'name' },
                                    { title: 'AED', dataIndex: 'value', key: 'value', align: 'right',
                                      render: v => <strong>{(v ?? 0).toLocaleString()}</strong> },
                                ],
                                rows: (salesDashboard?.agents ?? []).map(a => ({ name: a.name, value: a[meta.col] ?? 0 })),
                            });
                        };

                        return (
                            <div className="flex gap-2 flex-wrap mb-2">
                                {[
                                    { label: "Today's Sales",  key: "daily_sales",    color: "#16a34a" },
                                    { label: "Daily Target",   key: "daily_target",   color: "#1d4ed8" },
                                    { label: "Month Sales",    key: "monthly_sales",  color: "#7c3aed" },
                                    { label: "Monthly Target", key: "monthly_target", color: "#b45309" },
                                ].map(({ label, key, color }) => (
                                    <Tooltip key={key} title="Click to see per-agent breakdown">
                                        <div
                                            onClick={() => !salesDashboardLoading && openSummaryModal(key)}
                                            className="border rounded p-2 drop-shadow flex-1 cursor-pointer hover:brightness-95 active:scale-95 transition-all"
                                            style={{ backgroundColor: "#f9f9f9", minWidth: 140 }}
                                        >
                                            <div style={{ fontSize: 11, color: "#888" }}>{label}</div>
                                            {salesDashboardLoading
                                                ? <Skeleton.Input active size="small" style={{ width: 100, marginTop: 4 }} />
                                                : <div style={{ fontSize: 18, fontWeight: 700, color }}>
                                                    AED {(s[key] ?? 0).toLocaleString()}
                                                  </div>
                                            }
                                        </div>
                                    </Tooltip>
                                ))}

                                {/* Daily achievement */}
                                <Tooltip title="Click to see per-agent daily breakdown">
                                    <div
                                        onClick={() => !salesDashboardLoading && openSummaryModal('daily_sales')}
                                        className="border rounded p-2 drop-shadow flex-1 cursor-pointer hover:brightness-95 active:scale-95 transition-all"
                                        style={{ backgroundColor: "#f9f9f9", minWidth: 190 }}
                                    >
                                        <div style={{ fontSize: 11, color: "#888" }}>Daily Achievement</div>
                                        {salesDashboardLoading
                                            ? <Skeleton.Input active size="small" style={{ width: "100%", marginTop: 4 }} />
                                            : <>
                                                <Progress percent={dailyPct} strokeColor={{ '0%': '#ef4444', '60%': '#f59e0b', '100%': '#16a34a' }} size="small" />
                                                <div style={{ fontSize: 10, color: "#555", textAlign: "right" }}>
                                                    AED {(s.daily_sales ?? 0).toLocaleString()} / {(s.daily_target ?? 0).toLocaleString()}
                                                </div>
                                              </>
                                        }
                                    </div>
                                </Tooltip>

                                {/* Monthly achievement */}
                                <Tooltip title="Click to see per-agent monthly breakdown">
                                    <div
                                        onClick={() => !salesDashboardLoading && openSummaryModal('monthly_sales')}
                                        className="border rounded p-2 drop-shadow flex-1 cursor-pointer hover:brightness-95 active:scale-95 transition-all"
                                        style={{ backgroundColor: "#f9f9f9", minWidth: 190 }}
                                    >
                                        <div style={{ fontSize: 11, color: "#888" }}>Monthly Achievement</div>
                                        {salesDashboardLoading
                                            ? <Skeleton.Input active size="small" style={{ width: "100%", marginTop: 4 }} />
                                            : <>
                                                <Progress percent={monthlyPct} strokeColor={{ '0%': '#ef4444', '60%': '#f59e0b', '100%': '#16a34a' }} size="small" />
                                                <div style={{ fontSize: 10, color: "#555", textAlign: "right" }}>
                                                    AED {(s.monthly_sales ?? 0).toLocaleString()} / {(s.monthly_target ?? 0).toLocaleString()}
                                                </div>
                                              </>
                                        }
                                    </div>
                                </Tooltip>
                            </div>
                        );
                    })()}

                    {/* ── Status colour legend ── */}
                    {(() => {
                        const STATUS_META = [
                            { label: "Getting Info",     key: "getting_info",     color: "#6366f1" },
                            { label: "Getting Price",    key: "getting_price",    color: "#f59e0b" },
                            { label: "Waiting Confirm",  key: "waiting_confirm",  color: "#3b82f6" },
                            { label: "Confirmed",        key: "confirmed",        color: "#16a34a" },
                            { label: "Out for Delivery", key: "out_for_delivery", color: "#8b5cf6" },
                            { label: "Delivered",        key: "delivered",        color: "#10b981" },
                            { label: "In Warranty",      key: "inside_warranty",  color: "#0891b2" },
                        ];

                        const openStatusModal = (statusMeta) => {
                            setDrilldown({
                                open: true,
                                title: `${statusMeta.label} — by Agent`,
                                columns: [
                                    { title: 'Agent', dataIndex: 'name', key: 'name' },
                                    { title: 'Count', dataIndex: 'count', key: 'count', align: 'right',
                                      render: v => <strong style={{ color: statusMeta.color }}>{v ?? 0}</strong> },
                                ],
                                rows: (salesDashboard?.agents ?? []).map(a => ({
                                    name: a.name,
                                    count: a.statuses?.[statusMeta.key] ?? 0,
                                })),
                            });
                        };

                        const openAgentStatusModal = (agent, statusMeta) => {
                            setDrilldown({
                                open: true,
                                title: `${agent.name} — ${statusMeta.label}`,
                                columns: [
                                    { title: 'Detail', dataIndex: 'detail', key: 'detail' },
                                    { title: 'Value',  dataIndex: 'value',  key: 'value', align: 'right' },
                                ],
                                rows: [
                                    { detail: 'Agent',  value: agent.name },
                                    { detail: 'Status', value: statusMeta.label },
                                    { detail: 'Count',  value: agent.statuses?.[statusMeta.key] ?? 0 },
                                ],
                            });
                        };

                        const agentsWithColors = (salesDashboard?.agents ?? []).map((a, i) => ({
                            ...a,
                            color: AGENT_COLORS[i % AGENT_COLORS.length],
                        }));
                        const dailySalesTotal   = agentsWithColors.reduce((s, a) => s + (a.daily_sales   ?? 0), 0);
                        const monthlySalesTotal = agentsWithColors.reduce((s, a) => s + (a.monthly_sales ?? 0), 0);

                        return (
                            <>
                                {/* Legend row */}
                                <div className="flex gap-3 mb-1 flex-wrap" style={{ fontSize: 10, color: "#666" }}>
                                    {STATUS_META.map(({ label, key, color }) => (
                                        <Tooltip key={key} title={`Click to see all agents' ${label} count`}>
                                            <div
                                                className="flex items-center gap-1 cursor-pointer hover:opacity-70 transition-opacity"
                                                onClick={() => openStatusModal({ label, key, color })}
                                            >
                                                <div style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: color }} />
                                                {label}
                                            </div>
                                        </Tooltip>
                                    ))}
                                </div>

                                {/* ── Per-agent cards + pie panel ── */}
                                <div className="flex gap-2 items-start">
                                    {/* scrollable cards */}
                                    <div className="flex gap-2 overflow-x-auto pb-1 flex-1 min-w-0" style={{ alignItems: "stretch" }}>
                                    {salesDashboardLoading
                                        ? Array.from({ length: 4 }).map((_, i) => (
                                            <div key={i} className="border rounded p-2 drop-shadow" style={{ minWidth: 256, backgroundColor: "#f9f9f9" }}>
                                                <Skeleton active paragraph={{ rows: 6 }} />
                                            </div>
                                        ))
                                        : agentsWithColors.map((agent) => {
                                            const dailyPct = agent.daily_target
                                                ? Math.min(100, +((agent.daily_sales / agent.daily_target) * 100).toFixed(1))
                                                : 0;

                                            return (
                                                <div
                                                    key={agent.id}
                                                    className="border rounded drop-shadow flex-none"
                                                    style={{ minWidth: 256, backgroundColor: "#f9f9f9" }}
                                                >
                                                    {/* Agent header */}
                                                    <div className="flex items-center gap-2 px-2 pt-2 pb-1 border-b" style={{ backgroundColor: "#e8edf5", borderRadius: "7px 7px 0 0" }}>
                                                        <div style={{
                                                            width: 32, height: 32, borderRadius: "50%",
                                                            backgroundColor: agent.color, color: "white",
                                                            display: "flex", alignItems: "center", justifyContent: "center",
                                                            fontWeight: 700, fontSize: 13, flexShrink: 0,
                                                        }}>
                                                            {agent.name?.split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase()}
                                                        </div>
                                                        <div style={{ overflow: "hidden" }}>
                                                            <div style={{ fontWeight: 700, fontSize: 13, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                                                {agent.name}
                                                            </div>
                                                            <div style={{ fontSize: 10, color: "#555" }}>
                                                                {agent.active_chats ?? 0} active chat{agent.active_chats !== 1 ? "s" : ""}
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Status grid: 2 columns */}
                                                    <div className="px-2 pt-1.5" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4 }}>
                                                        {STATUS_META.map((sm) => (
                                                            <Tooltip key={sm.key} title={`Click to see details`}>
                                                                <div
                                                                    className="border rounded flex items-center justify-between px-1.5 py-0.5 cursor-pointer hover:brightness-95 active:scale-95 transition-all"
                                                                    style={{ backgroundColor: "white", fontSize: 11 }}
                                                                    onClick={() => openAgentStatusModal(agent, sm)}
                                                                >
                                                                    <div style={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: sm.color, flexShrink: 0 }} />
                                                                    <span style={{ flex: 1, marginLeft: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "#444" }}>
                                                                        {sm.label}
                                                                    </span>
                                                                    <span style={{ fontWeight: 700, marginLeft: 4, color: sm.color }}>
                                                                        {agent.statuses?.[sm.key] ?? 0}
                                                                    </span>
                                                                </div>
                                                            </Tooltip>
                                                        ))}
                                                    </div>

                                                    {/* Daily sales progress — clickable */}
                                                    <Tooltip title="Click to see daily sales detail">
                                                        <div
                                                            className="px-2 pt-2 pb-2 cursor-pointer hover:brightness-95 transition-all"
                                                            onClick={() => setDrilldown({
                                                                open: true,
                                                                title: `${agent.name} — Daily Sales`,
                                                                columns: [
                                                                    { title: 'Metric', dataIndex: 'metric', key: 'metric' },
                                                                    { title: 'Value',  dataIndex: 'value',  key: 'value', align: 'right',
                                                                      render: v => <strong>{v}</strong> },
                                                                ],
                                                                rows: [
                                                                    { metric: 'Daily Sales',   value: `AED ${(agent.daily_sales ?? 0).toLocaleString()}` },
                                                                    { metric: 'Daily Target',  value: `AED ${(agent.daily_target ?? 0).toLocaleString()}` },
                                                                    { metric: 'Achievement',   value: `${dailyPct}%` },
                                                                    { metric: 'Remaining',     value: `AED ${Math.max(0, (agent.daily_target ?? 0) - (agent.daily_sales ?? 0)).toLocaleString()}` },
                                                                ],
                                                            })}
                                                        >
                                                            <div className="flex justify-between" style={{ fontSize: 10, color: "#555", marginBottom: 2 }}>
                                                                <span>Daily Sales</span>
                                                                <span style={{ fontWeight: 700 }}>
                                                                    AED {(agent.daily_sales ?? 0).toLocaleString()}
                                                                    <span style={{ fontWeight: 400, color: "#999" }}> / {(agent.daily_target ?? 0).toLocaleString()}</span>
                                                                </span>
                                                            </div>
                                                            <Progress
                                                                percent={dailyPct}
                                                                size="small"
                                                                strokeColor={dailyPct >= 100 ? '#16a34a' : dailyPct >= 60 ? '#f59e0b' : '#ef4444'}
                                                                showInfo={false}
                                                            />
                                                            <div style={{ fontSize: 10, color: "#888", textAlign: "right" }}>{dailyPct}%</div>
                                                        </div>
                                                    </Tooltip>
                                                </div>
                                            );
                                        })
                                    }
                                </div>{/* end scrollable cards */}

                                    {/* ── Pie chart panel ── */}
                                    {!salesDashboardLoading && agentsWithColors.length > 0 && (
                                        <div className="border rounded drop-shadow flex-none"
                                            style={{ width: 176, backgroundColor: "#f9f9f9", padding: "10px 8px" }}>
                                            {/* Daily sales pie */}
                                            <div style={{ marginBottom: 12 }}>
                                                <div style={{ fontSize: 10, color: "#888", textAlign: "center", marginBottom: 4 }}>Daily Sales Share</div>
                                                <DonutChart
                                                    size={110}
                                                    thickness={22}
                                                    data={agentsWithColors.map(a => ({ value: a.daily_sales ?? 0, color: a.color }))}
                                                />
                                                <div style={{ marginTop: 6 }}>
                                                    {agentsWithColors.map(a => (
                                                        <div key={a.id} style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 2 }}>
                                                            <div style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: a.color, flexShrink: 0 }} />
                                                            <span style={{ fontSize: 10, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "#555" }}>
                                                                {a.name?.split(" ")[0]}
                                                            </span>
                                                            <span style={{ fontSize: 10, fontWeight: 700, color: a.color }}>
                                                                {dailySalesTotal > 0 ? ((a.daily_sales / dailySalesTotal) * 100).toFixed(0) : 0}%
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                            {/* Monthly sales pie */}
                                            <div style={{ borderTop: "1px solid #e5e7eb", paddingTop: 10 }}>
                                                <div style={{ fontSize: 10, color: "#888", textAlign: "center", marginBottom: 4 }}>Monthly Sales Share</div>
                                                <DonutChart
                                                    size={110}
                                                    thickness={22}
                                                    data={agentsWithColors.map(a => ({ value: a.monthly_sales ?? 0, color: a.color }))}
                                                />
                                                <div style={{ marginTop: 6 }}>
                                                    {agentsWithColors.map(a => (
                                                        <div key={a.id} style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 2 }}>
                                                            <div style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: a.color, flexShrink: 0 }} />
                                                            <span style={{ fontSize: 10, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "#555" }}>
                                                                {a.name?.split(" ")[0]}
                                                            </span>
                                                            <span style={{ fontSize: 10, fontWeight: 700, color: a.color }}>
                                                                {monthlySalesTotal > 0 ? ((a.monthly_sales / monthlySalesTotal) * 100).toFixed(0) : 0}%
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>{/* end cards + pie outer flex */}

                                {/* ── Demo confetti triggers (remove when wired to real events) ── */}
                                <div className="flex gap-2 mt-2 flex-wrap">
                                    <button
                                        onClick={() => triggerConfetti('🎉 Order Confirmed!')}
                                        className="border rounded px-3 py-1 text-xs cursor-pointer"
                                        style={{ backgroundColor: "#dcfce7", borderColor: "#16a34a", color: "#15803d" }}
                                    >
                                        Test: Order Confirmed 🎉
                                    </button>
                                    <button
                                        onClick={() => triggerConfetti('🏆 Daily Target Reached!')}
                                        className="border rounded px-3 py-1 text-xs cursor-pointer"
                                        style={{ backgroundColor: "#dbeafe", borderColor: "#1d4ed8", color: "#1e40af" }}
                                    >
                                        Test: Target Reached 🏆
                                    </button>
                                </div>
                            </>
                        );
                    })()}
                </div>

            </div >

            <ToastContainer />
        </div >
    );
}

export default DashboardV2;